import React, { useContext, useEffect, useRef, useState } from "react";
import { Button, Spin, Empty, Collapse, Tabs } from "antd";
import { PrinterOutlined, LoadingOutlined } from "@ant-design/icons";
import { useReactToPrint } from "react-to-print";
import { Link, useSearchParams } from "react-router-dom";
import { AuthContext } from "contexts";
import RadarChart from "components/RadarChart";
import StoryThemeCircle from "components/StoryThemeCircle";
import { REVIEW_DIMENSIONS, optionLabel, getReport } from "services/holistic";

const gradeText = (codes = []) =>
  codes.map((c) => optionLabel("GRADE_LEVEL", c)).join(", ");

// heading shown above the STUDENT-filled data of each section (so parents can tell
// which part the student wrote vs the advisor's score/notes)
const STUDENT_HEADINGS = {
  universities: "Сурагчийн зорьж буй их сургуулиуд",
  extracurriculars: "Сурагчийн хичээлээс гадуурх үйл ажиллагаанууд (ХГҮА)",
  honors: "Сурагчийн шагнал, амжилтууд",
  recLetters: "Сурагчийн сонгосон захидал авах багш нар",
  vision: "Сурагчийн тодорхойлсон хувь нэмэр оруулах асуудлууд",
  passion: "Сурагчийн тодорхойлсон мэргэжил, карьерын чиг баримжаа",
  exams: "Сурагчийн академик шалгалтын оноо",
};

// Per-section renderer for the student's submitted content.
const SectionContent = ({ sectionKey, items = [] }) => {
  if (!items.length)
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Мэдээлэл оруулаагүй" />;
  const row = "border border-slate-300 px-2 py-1 align-top text-[12px]";
  const th = "border border-slate-300 px-2 py-1 text-left text-[11px] font-semibold bg-amber-50 text-slate-700";
  switch (sectionKey) {
    case "vision":
      return items.map((it, i) => (
        <div key={i} className="mb-3">
          <div className="font-semibold">{i + 1}. {it.socialIssue}</div>
          <div className="text-slate-600 text-[13px]">{it.rootCause}</div>
          <div className="text-slate-600 text-[13px] italic">→ {it.contribution}</div>
        </div>
      ));
    case "passion":
      return items.map((it, i) => (
        <div key={i} className="mb-3">
          <div className="font-semibold">{i + 1}. {it.profession}</div>
          <div className="text-slate-600 text-[13px]">{it.motivation}</div>
          <div className="text-slate-600 text-[13px] italic">→ {it.experience}</div>
        </div>
      ));
    case "recLetters":
      return items.map((it, i) => (
        <div key={i} className="mb-2">
          <span className="font-semibold">{it.teacher}</span>
          <span className="text-slate-600 text-[13px]"> — {it.memories}</span>
        </div>
      ));
    case "honors":
      return (
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className={th}>Түвшин</th>
              <th className={th}>Анги</th>
              <th className={th}>Тайлбар</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i}>
                <td className={row}>{optionLabel("RECOGNITION_LEVEL", it.recognitionLevel)}</td>
                <td className={row}>{gradeText(it.gradeLevels)}</td>
                <td className={row}>{it.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    case "extracurriculars":
      return (
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className={th}>Төрөл</th>
              <th className={th}>Албан тушаал · Байгууллага</th>
              <th className={th}>Тайлбар</th>
              <th className={th}>Анги · цаг</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i}>
                <td className={row}>{optionLabel("ACTIVITY_TYPE", it.activityType)}</td>
                <td className={row}>{it.position} · {it.organization}</td>
                <td className={row}>{it.description}</td>
                <td className={row}>{gradeText(it.gradeLevels)} · {it.hoursPerWeek}ц/7хон · {it.weeksPerYear}7хон/жил</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    case "universities":
      return (
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className={th}>Улс</th>
              <th className={th}>Сургууль</th>
              <th className={th}>Илгээх шалтгаан</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i}>
                <td className={row}>{it.country}</td>
                <td className={row + " font-semibold"}>{it.name}</td>
                <td className={row}>{it.reasons}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    case "exams":
      return (
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className={th}>Шалгалт</th>
              <th className={th}>Төлөв</th>
              <th className={th}>Албан ёсны</th>
              <th className={th}>Mock</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i}>
                <td className={row}>{optionLabel("EXAM_TYPE", it.examType)}</td>
                <td className={row}>{it.taken ? "Өгсөн" : "Өгөөгүй"}</td>
                <td className={row}>Албан ёсны: {it.officialScore || "-"}</td>
                <td className={row}>Mock: {it.mockScore || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    default:
      return null;
  }
};

// One page, 3 in-page tabs (no route change). Data is fetched once.
function CollegePrepReport() {
  const { state } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const printRef = useRef();
  const [searchParams] = useSearchParams();
  const studentId = searchParams.get("student") || undefined;

  useEffect(() => {
    setLoading(true);
    getReport({ studentId })
      .then(setData)
      .finally(() => setLoading(false));
  }, [studentId]);

  const handlePrint = useReactToPrint({ content: () => printRef.current });

  const studentName =
    [state?.studentInfo?.profile?.lastName, state?.studentInfo?.profile?.firstName]
      .filter(Boolean)
      .join(" ") || "Сурагч";

  const version = data?.version;
  const reviewed = !!data?.reviewed;
  // parents (viewing via ?student=) don't see the student-filled data the advisor hid
  const isParentView = !!studentId;
  const hideStudent = isParentView && !!version?.hideStudentData;
  const scoreOf = (code) =>
    version?.reviewScores?.find((r) => r.dimension === code)?.score ?? 0;
  const noteOf = (code) =>
    version?.reviewScores?.find((r) => r.dimension === code);

  const radarData = REVIEW_DIMENSIONS.map((d) => ({ label: d.label, value: scoreOf(d.code) }));

  if (loading && !data)
    return (
      <div className="pt-[120px] flex justify-center">
        <Spin indicator={<LoadingOutlined style={{ fontSize: 28 }} />} />
      </div>
    );

  // Parent view: nothing is shown until the advisor releases (Сурагчид харуулах) the report.
  if (isParentView && !reviewed)
    return (
      <div className="max-w-2xl mx-auto px-4 pt-[100px] pb-16 text-center">
        <Link to="/college-prep/children">
          <Button className="mb-6">← Буцах</Button>
        </Link>
        <Empty
          description={
            <span className="text-slate-500">
              Зөвлөгч багш энэ сурагчийн тайланг хараахан нээгээгүй байна.
            </span>
          }
        />
      </div>
    );

  // ── Tab 1: Цогц тайлан (radar + 7 dimensions) ──
  const overviewTab = (
    <>
      <div className="flex flex-col items-center mb-6">
        {reviewed ? (
          <>
            <div className="text-base font-semibold text-slate-600 mb-2">
              Өргөдлийн дундаж чанар:{" "}
              <span className="text-amber-500 text-4xl font-extrabold align-middle">{version?.overallScore}%</span>
            </div>
            <RadarChart data={radarData} max={100} size={380} />
          </>
        ) : (
          <div className="w-full bg-amber-50 border border-amber-200 rounded-md p-8 text-center text-amber-700">
            Зөвлөгч багш хянаж байна. Үнэлгээ дууссаны дараа график энд харагдана.
          </div>
        )}
      </div>

      {reviewed && (version?.overallReadiness || version?.progressSummary) && (
        <div className="mb-6 bg-primary/5 border border-primary/30 rounded-md p-4">
          <div className="font-bold text-primary mb-2">Ерөнхий үнэлгээ</div>
          {version.overallReadiness && (
            <p className="text-[15px] text-slate-900 whitespace-pre-line mb-2">{version.overallReadiness}</p>
          )}
          {version.progressSummary && (
            <p className="text-[14px] text-slate-600 whitespace-pre-line">{version.progressSummary}</p>
          )}
        </div>
      )}

      <Collapse
        defaultActiveKey={REVIEW_DIMENSIONS.map((d) => d.code)}
        items={REVIEW_DIMENSIONS.map((d, i) => {
          const note = noteOf(d.code);
          return {
            key: d.code,
            label: (
              <div className="w-full pr-2">
                <span className="text-primary font-semibold">1.{i + 1}. {d.label}</span>
              </div>
            ),
            children: (
              <>
                {reviewed && (
                  <div className="grid grid-cols-1 sm:grid-cols-[150px_1fr] gap-4 mb-4 pb-3 border-b border-slate-200">
                    <div>
                      <div className="text-primary font-bold text-[13px] mb-1">Сурагчийн явц</div>
                      <div className="text-amber-500 text-4xl font-extrabold leading-none">{scoreOf(d.code)}%</div>
                    </div>
                    <div>
                      <div className="text-primary font-bold text-[13px] mb-1">Явцын тайлбар</div>
                      <div className="text-[13px] text-slate-700 whitespace-pre-line">{note?.rubricDescription || "—"}</div>
                    </div>
                  </div>
                )}
                {reviewed && note?.advisorNote && (
                  <div className="mb-3 bg-amber-50 border-l-4 border-amber-400 rounded px-4 py-3">
                    <div className="font-bold text-amber-700 mb-1 text-[12px] uppercase tracking-wide">
                      ★ Зөвлөгч багшийн тэмдэглэл
                    </div>
                    <div className="text-[15px] font-medium text-slate-900 whitespace-pre-line">
                      {note.advisorNote}
                    </div>
                  </div>
                )}
                {(() => {
                  const allItems = data?.student?.[d.section] || [];
                  // parent view hides counselor-selected items (by index) for this section
                  const hiddenIdx =
                    (isParentView && version?.hiddenItems?.[d.section]) || [];
                  const visibleItems = allItems.filter((_, idx) => !hiddenIdx.includes(idx));
                  const allHidden = allItems.length > 0 && visibleItems.length === 0;
                  if (hideStudent || allHidden)
                    return (
                      <div className="text-[12px] text-slate-400 italic">
                        Сурагчийн бөглөсөн хэсгийг харуулаагүй.
                      </div>
                    );
                  return (
                    <>
                      <div className="font-bold text-primary text-[13px] mb-1">
                        {STUDENT_HEADINGS[d.section] || "Сурагчийн оруулсан мэдээлэл"}
                      </div>
                      <SectionContent sectionKey={d.section} items={visibleItems} />
                    </>
                  );
                })()}
              </>
            ),
          };
        })}
      />
    </>
  );

  // ── Tab 2: Өргөдлийн төлөвлөгөө (Story Building) ──
  const storySections = version
    ? [
        { label: "Яагаад? (Гүн гүнзгий шалтгаан)", value: version.storyWhy, color: "text-orange-600" },
        { label: "Тэр юу хийж бүтээсэн бэ?", value: version.storyWhat, color: "text-teal-600" },
        { label: "Хэрхэн?", value: version.storyHow, color: "text-primary" },
        { label: "Эдгээрийг хийж яг чадах уу?", value: version.storyCan, color: "text-amber-600" },
      ].filter((s) => (s.value || "").trim())
    : [];
  const hasTheme =
    version &&
    ((version.themeFuture || "").trim() ||
      (version.themeReason || "").trim() ||
      (version.themeStories || "").trim());

  const storyTab =
    !version?.storyPublished ? (
      <Empty description="Зөвлөгч багш хараахан төлөвлөгөө илгээгээгүй байна." />
    ) : !storySections.length && !hasTheme ? (
      <Empty description="Төлөвлөгөөний агуулга оруулаагүй байна." />
    ) : (
      <>
        {hasTheme && (
          <div className="flex justify-center mb-8">
            <StoryThemeCircle
              topText={(version.themeFuture || "").trim() ? `Ирээдүйд ${version.themeFuture}-ээр явах гэж байгаа` : ""}
              bottomText={(version.themeReason || "").trim() ? `Учир нь тэр ${version.themeReason}-г хүсдэг` : ""}
              centerText={version.themeStories || ""}
            />
          </div>
        )}
        {storySections.map((s, i) => (
          <div key={i} className="mb-6">
            <div className={`font-bold text-lg mb-1 ${s.color}`}>{s.label}</div>
            <p className="text-[15px] text-slate-700 leading-relaxed whitespace-pre-line">{s.value}</p>
          </div>
        ))}
      </>
    );

  // ── Tab 3: To-Do ──
  const todoTab =
    !version?.todoPublished ? (
      <Empty description="Зөвлөгч багш хараахан To-Do илгээгээгүй байна." />
    ) : (
      <>
        <div className="text-center text-lg font-extrabold text-primary mb-5">
          ЭНЭ ЗУН ЗААВАЛ БИЕЛҮҮЛЭХ “TO DO LIST”
        </div>
        {version.counselorMessage && (
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
            <div className="font-bold text-primary mb-1">Коунселор багшаас нь…</div>
            <p className="text-[14px] text-slate-700 whitespace-pre-line">{version.counselorMessage}</p>
          </div>
        )}
        {(version.todo || []).length === 0 ? (
          <Empty description="Зөвлөмж оруулаагүй байна." />
        ) : (
          (version.todo || []).map((t, i) => (
            <div key={i} className="mb-6 border-l-4 border-amber-400 bg-amber-50/40 rounded-r-md px-4 py-3">
              <div className="font-bold text-primary mb-1">Зөвлөмж №{i + 1}</div>
              <ul className="list-disc pl-6 text-[14px] text-slate-800">
                <li className="whitespace-pre-line">{t.advice}</li>
              </ul>
              {(t.importance || "").trim() && (
                <>
                  <div className="font-semibold text-amber-700 mt-2">Ач холбогдол, шалтгаан:</div>
                  <ul className="list-disc pl-6 text-[13px] text-slate-600">
                    <li className="whitespace-pre-line">{t.importance}</li>
                  </ul>
                </>
              )}
            </div>
          ))
        )}
      </>
    );

  return (
    <div className="max-w-4xl mx-auto px-4 pt-[80px] pb-16">
      <div className="noprint flex flex-wrap items-center justify-between gap-3 mb-4">
        <Link to={studentId ? "/college-prep/children" : "/college-prep"}>
          <Button>← Буцах</Button>
        </Link>
        <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>
          Хэвлэх / PDF
        </Button>
      </div>

      <div ref={printRef} className="bg-white p-6 border rounded-md">
        <div className="text-center mb-4">
          <div className="text-xl font-bold text-primary">
            ИХ СУРГУУЛИЙН БЭЛТГЭЛИЙН ЦОГЦ ТАЙЛАН
          </div>
          <div className="text-sm text-slate-500 mt-1">
            {studentName} · {data?.cycle} оны төгсөгч
            {version && version.advisor ? ` · Зөвлөгч: ${version.advisor}` : ""}
          </div>
        </div>

        <Tabs
          defaultActiveKey="overview"
          items={[
            { key: "overview", label: "Цогц тайлан", children: overviewTab },
            { key: "story", label: "Өргөдлийн төлөвлөгөө", children: storyTab },
            { key: "todo", label: "To-Do", children: todoTab },
          ]}
        />
      </div>
    </div>
  );
}

export default CollegePrepReport;
